import { useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { toast } from 'sonner';
import { 
  MoreVertical, 
  Undo2, 
  XCircle, 
  Settings2, 
  History,
  Loader2,
  AlertTriangle,
  Euro,
  Calendar,
  Copy,
  Car
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Mappa esiti per rollback
const ESITI_ROLLBACK_MAP = {
  "contratto_firmato": ["contratto_da_firmare", "approvato", "pratica_credito"],
  "contratto_da_firmare": ["approvato", "pratica_credito"],
  "approvato": ["pratica_credito", "richiesta_documentazione"],
  "pratica_credito": ["richiesta_documentazione", "accettato_agente"],
  "richiesta_documentazione": ["accettato_agente", "interessato_attesa"],
  "rifiutato": ["pratica_credito", "richiesta_documentazione", "accettato_agente"],
};

// Labels leggibili per gli esiti
const ESITI_LABELS = {
  "contratto_firmato": "Contratto Firmato",
  "contratto_da_firmare": "Contratto da Firmare",
  "approvato": "Approvato",
  "pratica_credito": "Pratica di Credito",
  "richiesta_documentazione": "Richiesta Documentazione",
  "accettato_agente": "Accettato (Agente)",
  "interessato_attesa": "Interessato in Attesa",
};

/**
 * AdminOfferControls - Menu azioni Admin per gestione avanzata offerte
 * 
 * Funzionalità:
 * - Override parametri (provvigione, scadenza)
 * - Rollback stato (torna indietro nel workflow)
 * - Annullamento offerta
 * - Visualizzazione log modifiche
 */
export const AdminOfferControls = ({ offer, onUpdate }) => {
  const { getAuthHeader, user } = useAuth();
  const [isOverrideOpen, setIsOverrideOpen] = useState(false);
  const [isRollbackOpen, setIsRollbackOpen] = useState(false);
  const [isCancelOpen, setIsCancelOpen] = useState(false);
  const [isMarkSoldOpen, setIsMarkSoldOpen] = useState(false);
  const [isLogOpen, setIsLogOpen] = useState(false);
  const [isCloneOpen, setIsCloneOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [logData, setLogData] = useState([]);
  
  // Form state
  const [overrideForm, setOverrideForm] = useState({
    provvigione_override: '',
    scadenza_override: '',
    note_override: ''
  });
  const [rollbackForm, setRollbackForm] = useState({
    target_esito: '',
    motivo: ''
  });
  const [cancelForm, setCancelForm] = useState({
    motivo: ''
  });
  const [markSoldForm, setMarkSoldForm] = useState({
    motivo: 'Veicolo non più disponibile'
  });
  
  // Clone form state - tutti i campi editabili
  const [cloneForm, setCloneForm] = useState({
    marca: '',
    modello: '',
    versione: '',
    colore: '',
    targa: '',
    optional_inclusi: '',
    alimentazione: 'Benzina',
    tipo_veicolo: 'nuovo',
    cambio: 'Manuale',
    tipologia_prodotto: 'Standard',
    disponibilita_immatricolazione: '',
    canone_mensile: '',
    anticipo: '0',
    durata_mesi: '',
    km_annui: '',
    km_illimitati: false,
    gomme_testo: '',
    franchigia_rca: '€ 250',
    franchigia_kasko: '1.000 / 10%',
    franchigia_if: '10%',
    provvigione_agenzia: '0',
    provvigione_agente: '0',
    note: ''
  });

  // Non mostrare se non è admin/super_admin/top_agent
  const isAdminRole = ['admin', 'super_admin', 'top_agent'].includes(user?.role);
  if (!isAdminRole) return null;
  
  // Non mostrare se l'offerta è già annullata
  if (offer.status === 'annullata') return null;

  // Calcola target validi per rollback
  const currentEsito = offer.esito;
  const currentStatus = offer.status;
  let validRollbackTargets = ESITI_ROLLBACK_MAP[currentEsito] || [];
  
  // Se è un ordine (status=contratto), può tornare a più stati
  if (currentStatus === 'contratto') {
    validRollbackTargets = ["contratto_firmato", "contratto_da_firmare", "approvato", "pratica_credito"];
  }

  // Handler Override
  const handleOverride = async () => {
    setIsLoading(true);
    try {
      const payload = {};
      if (overrideForm.provvigione_override) {
        payload.provvigione_override = parseFloat(overrideForm.provvigione_override);
      }
      if (overrideForm.scadenza_override) {
        payload.scadenza_override = overrideForm.scadenza_override;
      }
      if (overrideForm.note_override) {
        payload.note_override = overrideForm.note_override;
      }

      if (Object.keys(payload).length === 0) {
        toast.error('Specifica almeno un parametro da modificare');
        return;
      }

      await axios.post(
        `${API}/admin/offers/${offer.id}/override`,
        payload,
        { headers: getAuthHeader() }
      );
      
      toast.success('Parametri aggiornati con successo');
      setIsOverrideOpen(false);
      setOverrideForm({ provvigione_override: '', scadenza_override: '', note_override: '' });
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante l\'aggiornamento');
    } finally {
      setIsLoading(false);
    }
  };

  // Handler Rollback
  const handleRollback = async () => {
    if (!rollbackForm.target_esito) {
      toast.error('Seleziona lo stato di destinazione');
      return;
    }
    
    setIsLoading(true);
    try {
      const response = await axios.post(
        `${API}/admin/offers/${offer.id}/rollback`,
        rollbackForm,
        { headers: getAuthHeader() }
      );
      
      toast.success(response.data.message || `Offerta riportata a "${ESITI_LABELS[rollbackForm.target_esito]}"`);
      setIsRollbackOpen(false);
      setRollbackForm({ target_esito: '', motivo: '' });
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante il rollback');
    } finally {
      setIsLoading(false);
    }
  };

  // Handler Cancel
  const handleCancel = async () => {
    setIsLoading(true);
    try {
      const response = await axios.post(
        `${API}/admin/offers/${offer.id}/cancel`,
        cancelForm,
        { headers: getAuthHeader() }
      );
      
      toast.success(response.data.message || 'Offerta annullata. Verrà eliminata tra 2 giorni.');
      setIsCancelOpen(false);
      setCancelForm({ motivo: '' });
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante l\'annullamento');
    } finally {
      setIsLoading(false);
    }
  };

  // Handler Mark as Sold (Segna come Venduta)
  const handleMarkSold = async () => {
    setIsLoading(true);
    try {
      const response = await axios.post(
        `${API}/admin/offers/${offer.id}/mark-sold`,
        markSoldForm,
        { headers: getAuthHeader() }
      );
      
      toast.success(response.data.message || 'Offerta segnata come venduta. Verrà eliminata tra 15 giorni.');
      setIsMarkSoldOpen(false);
      setMarkSoldForm({ motivo: 'Veicolo non più disponibile' });
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante l\'operazione');
    } finally {
      setIsLoading(false);
    }
  };

  // Handler View Log
  const handleViewLog = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(
        `${API}/admin/offers/${offer.id}/log`,
        { headers: getAuthHeader() }
      );
      setLogData(response.data.modifications || []);
      setIsLogOpen(true);
    } catch (error) {
      toast.error('Errore nel caricamento dello storico');
    } finally {
      setIsLoading(false);
    }
  };

  // Handler Open Clone - precompila con dati offerta originale (arricchito da catalogo vehicles)
  const handleOpenClone = async () => {
    const vehicleInfo = offer.vehicle_info || {};
    // Normalizza alimentazione: se "Hybrid"/"Hybrida" generico, prova a derivare dal versione
    let alim = vehicleInfo.fuel || vehicleInfo.alimentazione || 'Benzina';
    if (/^hybrid/i.test(alim) && !/benzina|diesel/i.test(alim)) {
      const ver = (vehicleInfo.versione || '').toLowerCase();
      alim = ver.includes('diesel') ? 'Hybrida Diesel' : 'Hybrida Benzina';
    }
    // Tipologia prodotto: 4vantage / Longrun / CHtec se l'offerta originale lo era
    let tipologia = 'Standard';
    const tp = (vehicleInfo.tipologia_prodotto || '').toLowerCase();
    if (vehicleInfo.use_4vantage || tp === '4vantage') tipologia = '4vantage';
    if (vehicleInfo.use_longrun || tp === 'longrun') tipologia = 'Longrun';
    if (tp === 'chtec') tipologia = 'CHtec';
    const isLongrunOrig = tipologia === 'Longrun' || (offer.km_annui || 0) >= 999999;

    // Arricchisci con dati dal catalogo vehicles (solo se vehicle_id reale, non manuale)
    let veicolo = {};
    const vid = offer.vehicle_id;
    const isManualVid = !vid || /^(manual_|MAN-)/i.test(vid);
    if (!isManualVid) {
      try {
        const res = await axios.get(`${API}/vehicles/${vid}`, { headers: getAuthHeader() });
        veicolo = res.data || {};
      } catch {
        veicolo = {};
      }
    }

    // Pre-compila disponibilità/immatricolazione: priorità vehicle_info > catalogo
    const dataPre = vehicleInfo.data_disponibilita
      || vehicleInfo.data_immatricolazione
      || veicolo.disponibilita
      || veicolo.immatricolazione
      || '';
    // Optional inclusi: priorità vehicle_info > catalogo
    const optionalPre = vehicleInfo.optional_inclusi || veicolo.optional_inclusi || '';

    setCloneForm({
      marca: vehicleInfo.marca || veicolo.marca || '',
      modello: vehicleInfo.modello || veicolo.modello || '',
      versione: vehicleInfo.versione || veicolo.versione || '',
      colore: vehicleInfo.colore || veicolo.colore || '',
      targa: vehicleInfo.targa || veicolo.targa || '',
      optional_inclusi: optionalPre,
      alimentazione: alim,
      tipo_veicolo: vehicleInfo.tipo || veicolo.tipo || 'nuovo',
      cambio: vehicleInfo.cambio || veicolo.cambio || 'Manuale',
      tipologia_prodotto: tipologia,
      disponibilita_immatricolazione: dataPre,
      canone_mensile: offer.canone_finale?.toString() || '',
      anticipo: offer.anticipo?.toString() || '0',
      durata_mesi: offer.durata_mesi?.toString() || '',
      km_annui: isLongrunOrig ? '' : (offer.km_annui?.toString() || ''),
      km_illimitati: isLongrunOrig,
      gomme_testo: '',
      franchigia_rca: '€ 250',
      franchigia_kasko: '1.000 / 10%',
      franchigia_if: '10%',
      provvigione_agenzia: offer.provvigione_totale?.toString() || '0',
      provvigione_agente: offer.provvigione_agente?.toString() || '0',
      note: ''
    });
    setIsCloneOpen(true);
  };

  // Handler Clone - crea nuova offerta manuale
  const handleClone = async () => {
    // Validazione campi obbligatori
    if (!cloneForm.marca || !cloneForm.modello) {
      toast.error('Marca e Modello sono obbligatori');
      return;
    }
    if (!cloneForm.canone_mensile || parseFloat(cloneForm.canone_mensile) <= 0) {
      toast.error('Inserisci un canone mensile valido');
      return;
    }
    if (!cloneForm.durata_mesi || parseInt(cloneForm.durata_mesi) <= 0) {
      toast.error('Inserisci una durata valida');
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        marca: cloneForm.marca,
        modello: cloneForm.modello,
        versione: cloneForm.versione || '',
        colore: cloneForm.colore || '',
        targa: cloneForm.targa || '',
        optional_inclusi: cloneForm.optional_inclusi || '',
        alimentazione: cloneForm.alimentazione,
        tipo_veicolo: cloneForm.tipo_veicolo,
        cambio: cloneForm.cambio,
        tipologia_prodotto: cloneForm.tipologia_prodotto,
        disponibilita_immatricolazione: cloneForm.disponibilita_immatricolazione || '',
        canone_mensile: parseFloat(cloneForm.canone_mensile),
        anticipo: parseFloat(cloneForm.anticipo) || 0,
        durata_mesi: parseInt(cloneForm.durata_mesi),
        km_annui: cloneForm.km_illimitati ? 999999 : (parseInt(cloneForm.km_annui) || 10000),
        gomme_testo: cloneForm.gomme_testo || null,
        franchigia_rca: cloneForm.franchigia_rca,
        franchigia_kasko: cloneForm.franchigia_kasko,
        franchigia_if: cloneForm.franchigia_if,
        provvigione_agenzia: parseFloat(cloneForm.provvigione_agenzia) || 0,
        provvigione_agente: parseFloat(cloneForm.provvigione_agente) || 0,
        note: cloneForm.note || null
      };

      const response = await axios.post(
        `${API}/admin/offers/${offer.id}/clone`,
        payload,
        { headers: getAuthHeader() }
      );
      
      toast.success(response.data.message || 'Offerta manuale creata con successo');
      setIsCloneOpen(false);
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante la creazione');
    } finally {
      setIsLoading(false);
    }
  };

  // Clona e Sovrascrivi: disponibile sia per clienti Smart che Prospect

  return (
    <>
      {/* Menu Dropdown */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button 
            variant="ghost" 
            size="sm" 
            className="h-8 w-8 p-0"
            data-testid="admin-offer-menu"
          >
            <MoreVertical className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          <DropdownMenuLabel className="text-xs text-slate-500">
            Azioni Admin
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          
          {/* Clona e Sovrascrivi - Disponibile per clienti Smart e Prospect */}
          <DropdownMenuItem onClick={handleOpenClone} className="text-violet-600 focus:text-violet-600">
            <Copy className="w-4 h-4 mr-2" />
            Clona e Sovrascrivi
          </DropdownMenuItem>
          
          <DropdownMenuItem onClick={() => setIsOverrideOpen(true)}>
            <Settings2 className="w-4 h-4 mr-2" />
            Modifica Parametri
          </DropdownMenuItem>
          
          {validRollbackTargets.length > 0 && (
            <DropdownMenuItem onClick={() => setIsRollbackOpen(true)}>
              <Undo2 className="w-4 h-4 mr-2" />
              Rollback Stato
            </DropdownMenuItem>
          )}
          
          <DropdownMenuItem onClick={handleViewLog}>
            <History className="w-4 h-4 mr-2" />
            Storico Modifiche
          </DropdownMenuItem>
          
          <DropdownMenuSeparator />
          
          {/* Segna come Venduta - Solo se non già venduta */}
          {!offer.vehicle_sold && (
            <DropdownMenuItem 
              onClick={() => setIsMarkSoldOpen(true)}
              className="text-orange-600 focus:text-orange-600"
            >
              <Car className="w-4 h-4 mr-2" />
              Segna come Venduta
            </DropdownMenuItem>
          )}
          
          <DropdownMenuItem 
            onClick={() => setIsCancelOpen(true)}
            className="text-red-600 focus:text-red-600"
          >
            <XCircle className="w-4 h-4 mr-2" />
            Annulla Offerta
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Dialog Override */}
      <Dialog open={isOverrideOpen} onOpenChange={setIsOverrideOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings2 className="w-5 h-5 text-blue-600" />
              Modifica Parametri Offerta
            </DialogTitle>
            <DialogDescription>
              Sovrascrivi i valori calcolati. Le modifiche verranno registrate nel log.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Euro className="w-4 h-4" />
                Provvigione (€)
              </Label>
              <Input
                type="number"
                step="0.01"
                placeholder={`Attuale: €${offer.provvigione_agente?.toFixed(2) || '0.00'}`}
                value={overrideForm.provvigione_override}
                onChange={(e) => setOverrideForm({...overrideForm, provvigione_override: e.target.value})}
                data-testid="override-provvigione"
              />
              <p className="text-xs text-slate-500">
                Valore originale: €{offer.provvigione_original?.toFixed(2) || offer.provvigione_agente?.toFixed(2) || '0.00'}
              </p>
            </div>
            
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Data Scadenza
              </Label>
              <Input
                type="date"
                value={overrideForm.scadenza_override}
                onChange={(e) => setOverrideForm({...overrideForm, scadenza_override: e.target.value})}
                data-testid="override-scadenza"
              />
              <p className="text-xs text-slate-500">
                Scadenza attuale: {offer.scadenza || 'Non impostata'}
              </p>
            </div>
            
            <div className="space-y-2">
              <Label>Note (opzionale)</Label>
              <Textarea
                placeholder="Motivo della modifica..."
                value={overrideForm.note_override}
                onChange={(e) => setOverrideForm({...overrideForm, note_override: e.target.value})}
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsOverrideOpen(false)}>
              Annulla
            </Button>
            <Button onClick={handleOverride} disabled={isLoading}>
              {isLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Salva Modifiche
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Rollback */}
      <Dialog open={isRollbackOpen} onOpenChange={setIsRollbackOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Undo2 className="w-5 h-5 text-amber-600" />
              Rollback Stato Offerta
            </DialogTitle>
            <DialogDescription>
              Riporta l&apos;offerta a uno stato precedente. 
              {currentStatus === 'contratto' && (
                <span className="block mt-1 text-amber-600 font-medium">
                  ⚠️ L&apos;offerta verrà rimossa dal Portafoglio Clienti
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-sm">
                <span className="text-slate-500">Stato attuale:</span>{' '}
                <span className="font-semibold">{ESITI_LABELS[currentEsito] || currentEsito}</span>
              </p>
              {currentStatus === 'contratto' && (
                <p className="text-sm mt-1">
                  <span className="text-slate-500">Status:</span>{' '}
                  <span className="font-semibold text-violet-600">ORDINE (Portafoglio)</span>
                </p>
              )}
            </div>
            
            <div className="space-y-2">
              <Label>Stato di destinazione *</Label>
              <Select 
                value={rollbackForm.target_esito} 
                onValueChange={(v) => setRollbackForm({...rollbackForm, target_esito: v})}
              >
                <SelectTrigger data-testid="rollback-target">
                  <SelectValue placeholder="Seleziona stato..." />
                </SelectTrigger>
                <SelectContent>
                  {validRollbackTargets.map(esito => (
                    <SelectItem key={esito} value={esito}>
                      {ESITI_LABELS[esito] || esito}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Motivo (opzionale)</Label>
              <Textarea
                placeholder="Motivo del rollback..."
                value={rollbackForm.motivo}
                onChange={(e) => setRollbackForm({...rollbackForm, motivo: e.target.value})}
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsRollbackOpen(false)}>
              Annulla
            </Button>
            <Button 
              onClick={handleRollback} 
              disabled={isLoading || !rollbackForm.target_esito}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {isLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Conferma Rollback
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Cancel */}
      <Dialog open={isCancelOpen} onOpenChange={setIsCancelOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Annulla Offerta
            </DialogTitle>
            <DialogDescription>
              L&apos;offerta verrà annullata e rimossa dal portafoglio attivo.
              Rimarrà in archivio per 2 giorni, poi verrà eliminata definitivamente.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="p-3 bg-red-50 rounded-lg border border-red-200">
              <p className="text-sm text-red-800">
                <strong>Attenzione:</strong> Questa azione non può essere annullata dopo 2 giorni.
              </p>
            </div>
            
            <div className="space-y-2">
              <Label>Motivo annullamento (opzionale)</Label>
              <Textarea
                placeholder="Es: Errore di inserimento, Cliente ha rinunciato..."
                value={cancelForm.motivo}
                onChange={(e) => setCancelForm({...cancelForm, motivo: e.target.value})}
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCancelOpen(false)}>
              Torna Indietro
            </Button>
            <Button 
              onClick={handleCancel} 
              disabled={isLoading}
              variant="destructive"
            >
              {isLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Conferma Annullamento
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Mark as Sold (Segna come Venduta) */}
      <Dialog open={isMarkSoldOpen} onOpenChange={setIsMarkSoldOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-600">
              <Car className="w-5 h-5" />
              Segna come Venduta
            </DialogTitle>
            <DialogDescription>
              Il veicolo non è più disponibile. L&apos;offerta verrà marcata come 
              &quot;Non più disponibile&quot; e rimossa automaticamente tra 4 giorni.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="p-3 bg-orange-50 rounded-lg border border-orange-200">
              <p className="text-sm text-orange-800">
                <strong>Effetti:</strong>
              </p>
              <ul className="text-sm text-orange-700 mt-1 list-disc list-inside">
                <li>L&apos;offerta mostrerà il timbro &quot;Veicolo Venduto&quot;</li>
                <li>Non sarà più possibile modificare l&apos;offerta</li>
                <li>Verrà eliminata automaticamente tra 4 giorni</li>
              </ul>
            </div>
            
            <div className="space-y-2">
              <Label>Motivo (opzionale)</Label>
              <Textarea
                placeholder="Es: Veicolo venduto a terzi, Non più in stock..."
                value={markSoldForm.motivo}
                onChange={(e) => setMarkSoldForm({...markSoldForm, motivo: e.target.value})}
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsMarkSoldOpen(false)}>
              Annulla
            </Button>
            <Button 
              onClick={handleMarkSold} 
              disabled={isLoading}
              className="bg-orange-600 hover:bg-orange-700"
            >
              {isLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Conferma
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Log */}
      <Dialog open={isLogOpen} onOpenChange={setIsLogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-slate-600" />
              Storico Modifiche
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            {logData.length === 0 ? (
              <p className="text-center text-slate-500 py-8">
                Nessuna modifica registrata
              </p>
            ) : (
              <div className="space-y-3">
                {logData.map((entry) => (
                  <div key={`${entry.timestamp}-${entry.action}`} className="p-3 bg-slate-50 rounded-lg border text-sm">
                    <div className="flex justify-between items-start mb-2">
                      <span className={`font-semibold ${
                        entry.action === 'cancel' ? 'text-red-600' :
                        entry.action === 'rollback' ? 'text-amber-600' : 'text-blue-600'
                      }`}>
                        {entry.action === 'override' && '📝 Override Parametri'}
                        {entry.action === 'rollback' && '↩️ Rollback Stato'}
                        {entry.action === 'cancel' && '❌ Annullamento'}
                      </span>
                      <span className="text-xs text-slate-400">
                        {new Date(entry.timestamp).toLocaleString('it-IT')}
                      </span>
                    </div>
                    <p className="text-slate-600">
                      <span className="text-slate-400">Da:</span> {entry.admin_name}
                    </p>
                    {entry.changes && Object.keys(entry.changes).length > 0 && (
                      <div className="mt-2 text-xs">
                        {Object.entries(entry.changes).map(([key, val]) => (
                          <p key={key} className="text-slate-500">
                            {key}: {val.old} → {val.new}
                          </p>
                        ))}
                      </div>
                    )}
                    {entry.from_esito && (
                      <p className="mt-1 text-xs text-slate-500">
                        {ESITI_LABELS[entry.from_esito] || entry.from_esito} → {ESITI_LABELS[entry.to_esito] || entry.to_esito}
                      </p>
                    )}
                    {entry.motivo && (
                      <p className="mt-1 text-xs italic text-slate-500">
                        Motivo: {entry.motivo}
                      </p>
                    )}
                    {entry.note && (
                      <p className="mt-1 text-xs italic text-slate-500">
                        Note: {entry.note}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsLogOpen(false)}>
              Chiudi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Clona e Sovrascrivi */}
      <Dialog open={isCloneOpen} onOpenChange={setIsCloneOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-violet-700">
              <Copy className="w-5 h-5" />
              Clona e Sovrascrivi
            </DialogTitle>
            <DialogDescription>
              Crea una nuova offerta manuale con parametri personalizzati. 
              L'offerta manterrà il legame con l'agente e il cliente originale.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Sezione Veicolo */}
            <div className="space-y-3">
              <h4 className="font-semibold text-sm text-slate-700 border-b pb-1">🚗 Veicolo</h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Marca *</Label>
                  <Input
                    value={cloneForm.marca}
                    onChange={(e) => setCloneForm({...cloneForm, marca: e.target.value})}
                    placeholder="Es: FIAT"
                    data-testid="clone-marca"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Modello *</Label>
                  <Input
                    value={cloneForm.modello}
                    onChange={(e) => setCloneForm({...cloneForm, modello: e.target.value})}
                    placeholder="Es: 500"
                    data-testid="clone-modello"
                  />
                </div>
                <div className="space-y-1 col-span-2">
                  <Label className="text-xs">Versione</Label>
                  <Input
                    value={cloneForm.versione}
                    onChange={(e) => setCloneForm({...cloneForm, versione: e.target.value})}
                    placeholder="Es: 1.0 Hybrid Icon"
                  />
                </div>
                <div className="space-y-1 col-span-2">
                  <Label className="text-xs">Colore</Label>
                  <Input
                    value={cloneForm.colore}
                    onChange={(e) => setCloneForm({...cloneForm, colore: e.target.value})}
                    placeholder="Es: Bianco perlato"
                    data-testid="clone-colore"
                  />
                </div>
                <div className="space-y-1 col-span-2">
                  <Label className="text-xs">Targa (per veicoli Usato)</Label>
                  <Input
                    value={cloneForm.targa}
                    onChange={(e) => setCloneForm({...cloneForm, targa: e.target.value.toUpperCase()})}
                    placeholder="Es: AB123CD"
                    data-testid="clone-targa"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Alimentazione</Label>
                  <Select value={cloneForm.alimentazione} onValueChange={(v) => setCloneForm({...cloneForm, alimentazione: v})}>
                    <SelectTrigger data-testid="clone-alimentazione">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Benzina">Benzina</SelectItem>
                      <SelectItem value="Diesel">Diesel</SelectItem>
                      <SelectItem value="Hybrida Benzina">Hybrida Benzina</SelectItem>
                      <SelectItem value="Hybrida Diesel">Hybrida Diesel</SelectItem>
                      <SelectItem value="Electric">Elettrico</SelectItem>
                      <SelectItem value="GPL">GPL</SelectItem>
                      <SelectItem value="Metano">Metano</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Tipo</Label>
                  <Select value={cloneForm.tipo_veicolo} onValueChange={(v) => setCloneForm({...cloneForm, tipo_veicolo: v})}>
                    <SelectTrigger data-testid="clone-tipo">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="nuovo">Nuovo</SelectItem>
                      <SelectItem value="usato">Usato</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Trasmissione</Label>
                  <Select value={cloneForm.cambio} onValueChange={(v) => setCloneForm({...cloneForm, cambio: v})}>
                    <SelectTrigger data-testid="clone-cambio">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Automatico">Automatico</SelectItem>
                      <SelectItem value="Manuale">Manuale</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Tipologia Prodotto</Label>
                  <Select value={cloneForm.tipologia_prodotto} onValueChange={(v) => setCloneForm({...cloneForm, tipologia_prodotto: v, km_illimitati: v === 'Longrun' ? true : cloneForm.km_illimitati})}>
                    <SelectTrigger data-testid="clone-tipologia-prodotto">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Standard">Standard</SelectItem>
                      <SelectItem value="4vantage">4vantage</SelectItem>
                      <SelectItem value="Longrun">Longrun</SelectItem>
                      <SelectItem value="CHtec">CHtec (prodotto tecnico)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1 col-span-2">
                  <Label className="text-xs">
                    {cloneForm.tipo_veicolo === 'usato' ? 'Immatricolata' : 'Disponibilità'}
                  </Label>
                  <Input
                    value={cloneForm.disponibilita_immatricolazione}
                    onChange={(e) => setCloneForm({...cloneForm, disponibilita_immatricolazione: e.target.value})}
                    placeholder={cloneForm.tipo_veicolo === 'usato' ? 'Es: 06/2022' : 'Es: Pronta consegna / 09/2026'}
                    data-testid="clone-disponibilita"
                  />
                </div>
                <div className="space-y-1 col-span-2">
                  <Label className="text-xs">Optional Inclusi</Label>
                  <Textarea
                    value={cloneForm.optional_inclusi}
                    onChange={(e) => setCloneForm({...cloneForm, optional_inclusi: e.target.value})}
                    placeholder="Es: Tetto panoramico, Cerchi 18&quot;, Pelle, Navigatore..."
                    rows={3}
                    data-testid="clone-optional"
                  />
                </div>
              </div>
            </div>

            {/* Sezione Economico/Contrattuale */}
            <div className="space-y-3">
              <h4 className="font-semibold text-sm text-slate-700 border-b pb-1">💶 Economico / Contrattuale</h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Canone Mensile (€ IVA esclusa) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={cloneForm.canone_mensile}
                    onChange={(e) => setCloneForm({...cloneForm, canone_mensile: e.target.value})}
                    placeholder="Es: 450.00"
                    data-testid="clone-canone"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Anticipo (€)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={cloneForm.anticipo}
                    onChange={(e) => setCloneForm({...cloneForm, anticipo: e.target.value})}
                    placeholder="0"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Durata (mesi) *</Label>
                  <Input
                    type="number"
                    value={cloneForm.durata_mesi}
                    onChange={(e) => setCloneForm({...cloneForm, durata_mesi: e.target.value})}
                    placeholder="Es: 48"
                    data-testid="clone-durata"
                  />
                </div>
                <div className="space-y-1 col-span-2">
                  <Label className="text-xs">Km Annui</Label>
                  <div className="flex items-center gap-2 mb-1">
                    <Checkbox
                      id="clone-km-illimitati"
                      checked={!!cloneForm.km_illimitati}
                      onCheckedChange={(checked) => setCloneForm({...cloneForm, km_illimitati: !!checked})}
                      data-testid="clone-km-illimitati"
                    />
                    <Label htmlFor="clone-km-illimitati" className="text-xs font-normal cursor-pointer">
                      Km illimitati
                    </Label>
                  </div>
                  <Input
                    type="number"
                    value={cloneForm.km_illimitati ? '' : cloneForm.km_annui}
                    onChange={(e) => setCloneForm({...cloneForm, km_annui: e.target.value})}
                    placeholder={cloneForm.km_illimitati ? 'Illimitati' : 'Es: 15000'}
                    disabled={!!cloneForm.km_illimitati}
                    data-testid="clone-km-annui"
                  />
                </div>
              </div>
            </div>

            {/* Sezione Servizi/Assicurazioni */}
            <div className="space-y-3">
              <h4 className="font-semibold text-sm text-slate-700 border-b pb-1">🛡️ Servizi / Assicurazioni</h4>
              <div className="space-y-1">
                <Label className="text-xs">Pneumatici (testo libero)</Label>
                <Input
                  value={cloneForm.gomme_testo}
                  onChange={(e) => setCloneForm({...cloneForm, gomme_testo: e.target.value})}
                  placeholder="Es: 4 Invernali + 4 Estive incluse"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Franchigia RCA</Label>
                  <Input
                    value={cloneForm.franchigia_rca}
                    onChange={(e) => setCloneForm({...cloneForm, franchigia_rca: e.target.value})}
                    placeholder="€ 250"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Franchigia Kasko</Label>
                  <Input
                    value={cloneForm.franchigia_kasko}
                    onChange={(e) => setCloneForm({...cloneForm, franchigia_kasko: e.target.value})}
                    placeholder="1.000 / 10%"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Franchigia I&F</Label>
                  <Input
                    value={cloneForm.franchigia_if}
                    onChange={(e) => setCloneForm({...cloneForm, franchigia_if: e.target.value})}
                    placeholder="10%"
                  />
                </div>
              </div>
            </div>

            {/* Sezione Amministrazione */}
            <div className="space-y-3">
              <h4 className="font-semibold text-sm text-slate-700 border-b pb-1">📊 Amministrazione</h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Provvigione Agenzia (€)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={cloneForm.provvigione_agenzia}
                    onChange={(e) => setCloneForm({...cloneForm, provvigione_agenzia: e.target.value})}
                    placeholder="0"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Provvigione Agente (€)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={cloneForm.provvigione_agente}
                    onChange={(e) => setCloneForm({...cloneForm, provvigione_agente: e.target.value})}
                    placeholder="0"
                  />
                </div>
              </div>
            </div>

            {/* Note */}
            <div className="space-y-1">
              <Label className="text-xs">Note (opzionale)</Label>
              <Textarea
                value={cloneForm.note}
                onChange={(e) => setCloneForm({...cloneForm, note: e.target.value})}
                placeholder="Note aggiuntive per questa offerta manuale..."
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setIsCloneOpen(false)}>
              Annulla
            </Button>
            <Button 
              onClick={handleClone} 
              disabled={isLoading}
              className="bg-violet-600 hover:bg-violet-700"
            >
              {isLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
              Crea Offerta Manuale
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AdminOfferControls;
